// components/roster-helpers.js: Batch D pure seat-slot grouping.
//
// The room page shows ONE roster: two team columns (Team A takes seats 1
// and 3, Team B takes seats 2 and 4, partners sit across the table) plus a
// separate "Not on a team yet" pool above the columns. Each column always
// renders exactly SEATS_PER_TEAM seat rows; a seat holds a player or is
// open (null). Everyone appears exactly once: rosterIds() lists every id
// across the pool + columns for the UI (and tests) to check that.
//
// Ordering inside a column reuses team-helpers' stable rule (seat_index
// then join order); this module only shapes the buckets into fixed slots.

import { teamColumns } from './team-helpers.js';

/** Which table seats belong to each team (partners sit across). */
export const TEAM_SEATS = Object.freeze({ A: [1, 3], B: [2, 4] });

/** Seat rows rendered per team column, filled or open. */
export const SEATS_PER_TEAM = 2;

/**
 * Shape occupants into fixed seat slots.
 *
 * @template {{ occupant_id: string, team?: 'A'|'B'|null, seat_index?: number|null }} T
 * @param {T[]} occupants
 * @returns {{ A: Array<T|null>, B: Array<T|null>, unteamed: T[] }}
 */
export function rosterSeats(occupants) {
  const cols = teamColumns(occupants || []);
  const slot = (members) => {
    const rows = [...members];
    // A team can never hold more than SEATS_PER_TEAM players through the
    // Join buttons, but if it ever happens (a race), keep the extras in
    // additional visible rows rather than dropping a player silently.
    while (rows.length < SEATS_PER_TEAM) rows.push(null);
    return rows;
  };
  return { A: slot(cols.A), B: slot(cols.B), unteamed: cols.unteamed };
}

/**
 * Every occupant id across the pool + columns, in render order. Used to
 * prove no player is listed twice or missing.
 *
 * @param {{ A: Array<{occupant_id:string}|null>, B: Array<{occupant_id:string}|null>, unteamed: Array<{occupant_id:string}> }} seats
 * @returns {string[]}
 */
export function rosterIds(seats) {
  const ids = [];
  for (const o of seats.unteamed || []) ids.push(o.occupant_id);
  for (const team of ['A', 'B']) {
    for (const o of seats[team] || []) {
      if (o) ids.push(o.occupant_id);
    }
  }
  return ids;
}
